// ════════════════════════════════════════════════════════════
//  online/cards.js — THE FIGHTER'S CARD: every stat worth a fan's time, from the record (PURE)
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-22: "let's do a pass on the stats etc to make sure we can see all the cool various stats as well as a
//  'fan' maybe give them a baseball card style profile card?" — the front is who they are, the back is the numbers.
//  Pure: the same rows give the same card on every screen. Works for NPCs and Challengers alike (players and partners).
//  ui/BeingCard.jsx draws it.
// ════════════════════════════════════════════════════════════
import { cleanRows, standings, statOf, rankKeys, avgWin, REC, CUPS, finalR, BEINGS, CHALLENGERS, isChKey, divRec, nightTitleOf, isQual } from "./circuit.js";

const winnerOf = (r) => (r.w === 0 ? r.a : r.b);
/**
 * card(key, { seasonRows, weekRows, league (the day's), weekS, points (salvationOf), v3 }) →
 * { key, name, number, home, division, place, challenger,
 *   week: { w, l }, season: { w, l, pct, played }, rating, peak, streak, longest, form,
 *   fastest, avgWin, ruins, closeWins, upsets, animaPerWin,
 *   cups: [{ c, name, n }], apo: { w, l }, escaped, fell,
 *   best: { opp, at, label } | null, nemesis: { key, w, l } | null, victim: { key, w, l } | null,
 *   points: { score, cp, kp } | null }
 */
export function beingCard(key, { seasonRows = [], weekRows = [], league = null, weekS = null, points = null, v3 = false } = {}) {
  const rows = cleanRows(seasonRows), mine = rows.filter((r) => r.a === key || r.b === key);
  // the season, row by row: ratings going in (for upsets and the best win), the peak rating, the longest run
  let peak = null, longest = 0, upsets = 0, best = null;
  const Ss = standings(rows, null, (row, { pa }, S) => {
    if (row.a !== key && row.b !== key) return;
    const s = S.get(key), side = row.a === key ? 0 : 1, won = row.w === side;
    peak = peak == null ? s.elo : Math.max(peak, s.elo);
    if (s.streak > longest) longest = s.streak;
    const pMine = side === 0 ? pa : 1 - pa;
    if (won && pMine <= 0.35) upsets++;
    const opp = side === 0 ? row.b : row.a;
    if (won) { const oElo = S.get(opp) ? S.get(opp).elo : 1500; if (!best || oElo > best.elo) best = { opp, elo: oElo, at: row.at, row }; }
  });
  const s = statOf(Ss, key);
  const being = BEINGS.get(key), ch = CHALLENGERS.get(key);
  const division = league ? (league.circuit.includes(key) ? "circuit" : league.contenders.includes(key) ? "contenders" : null) : (being ? being.home : null);
  const W = weekS || standings(cleanRows(weekRows));
  const rec = division ? (v3 ? divRec(division === "circuit" ? "c" : "k") : REC.week) : REC.week;
  const place = division && league ? rankKeys(league[division], W, rec).indexOf(key) + 1 : null;
  const [ww, wl] = rec(statOf(W, key));
  // how they win
  const wins = mine.filter((r) => winnerOf(r) === key);
  const timed = wins.filter((r) => Number.isFinite(r.t) && r.t > 0 && r.why !== "paper");
  const fastest = timed.length ? Math.min(...timed.map((r) => r.t)) : null;
  const ruins = wins.filter((r) => r.why === "ruined").length, called = wins.filter((r) => r.why === "called");
  const closeWins = called.filter((r) => r.by && r.by !== "beings").length;
  // the cups they've won (finals), Apocalypse
  const cups = new Map();
  let quals = 0;   // (a Qualifier won is the way IN, not a crown: it's counted, not shelved)
  for (const r of mine) if (r.kind === "cup" && r.r === finalR(r.c) && winnerOf(r) === key) { if (isQual(r.c)) quals++; else cups.set(r.c, (cups.get(r.c) || 0) + 1); }
  // THE NIGHT'S TITLES (caP'n 2026-10-22): the Night Crown's winner is the NIGHT LORD when the Circuit is in the fight (either side — "if both are
  // participating the winner is Night Lord"); a fight between two Contenders makes the winner the NIGHT'S CHAMPION. A fighter's division that night = the
  // division of their league bouts that day (they fight in one per day).
  const night = { lord: 0, champ: 0 };
  for (const r of mine) if (r.kind === "cup" && r.c === "nc" && winnerOf(r) === key) night[nightTitleOf(rows, r)]++;   // (the rule lives in circuit.js nightTitleOf)
  const apo = { w: 0, l: 0 };
  for (const r of mine) if (r.kind === "apo") apo[winnerOf(r) === key ? "w" : "l"]++;
  // head to head: who they own, who owns them (at least 3 meetings)
  const h2h = new Map();
  for (const r of mine) { const opp = r.a === key ? r.b : r.a, x = h2h.get(opp) || { key: opp, w: 0, l: 0 }; x[winnerOf(r) === key ? "w" : "l"]++; h2h.set(opp, x); }
  const met = [...h2h.values()].filter((x) => x.w + x.l >= 3);
  const nemesis = met.filter((x) => x.l > x.w).sort((a, b) => (b.l - b.w) - (a.l - a.w) || b.l - a.l)[0] || null;
  const victim = met.filter((x) => x.w > x.l).sort((a, b) => (b.w - b.l) - (a.w - a.l) || b.w - a.w)[0] || null;
  const pts = points && points.get(key);
  return {
    key, name: (being || ch || {}).name || key, number: being ? being.n : null, home: being ? being.home : "challenger",
    challenger: isChKey(key), division, place: place > 0 ? place : null,
    week: { w: ww, l: wl }, season: { w: s.won, l: s.lost, played: s.played, pct: s.played ? s.won / s.played : null },
    rating: Math.round(weekS ? statOf(weekS, key).elo : s.elo), peak: peak == null ? null : Math.round(peak),
    streak: s.streak, longest, form: s.form.slice(),
    fastest, avgWin: s.timed ? avgWin(s) : null, ruins, closeWins, upsets, animaPerWin: s.wins ? Math.round(s.anima / s.wins) : null,
    cups: [...cups].map(([c, n]) => ({ c, name: CUPS[c] ? CUPS[c].name.replace(/^the /, "") : c, n })).sort((a, b) => ((CUPS[b.c] && CUPS[b.c].tier) || 0) - ((CUPS[a.c] && CUPS[a.c].tier) || 0)),
    quals, night, apo, escaped: !!(league && (league.escaped || []).includes(key)), fell: !!(league && (league.dropped || []).includes(key)),
    best: best ? { opp: best.opp, at: best.at, row: best.row } : null, nemesis, victim,
    points: pts ? { score: Math.round(pts.score), cp: Math.round(pts.cp), kp: Math.round(pts.kp) } : null,
  };
}
/** A one-word nickname for the card's front, from how they fight their season (display only). */
export function cardTag(c) {
  if (!c.season.played) return "Rookie";
  if (c.cups.some((x) => x.c === "f")) return "Champion";
  if (c.cups.some((x) => x.c === "s")) return "King";
  if (c.night && c.night.lord) return "Night Lord";
  if (c.night && c.night.champ) return "Night's Champion";
  if (c.longest >= 7) return "Streaker";
  if (c.upsets >= 5) return "Giant-killer";
  if (c.fastest != null && c.fastest <= 75) return "Lightning";
  if (c.closeWins >= 5) return "Clutch";
  if (c.escaped) return "Escape artist";
  if (c.season.pct != null && c.season.pct >= 0.6) return "Ace";
  return "Grinder";
}
